import { HeroCaretAnimation } from "@landing/components/landing/hero-caret-animation"
import { getBrackyNotificationClip, playSingleAudio } from "@landing/components/landing/LandingSounds"
import { useBrackyScale } from "@landing/hooks/use-bracky-scale"
import { useCallback, useEffect, useState } from "react"
import BrackyAvatar from "../madness/BrackyAvatar"

export function LandingHeroBrackyIntro() {
  const { activeMultiplier, isMobile, setIsAvatarHovered, setIsAvatarPressed, computedScale } = useBrackyScale()

  const handleScrollToBracky = useCallback(() => {
    if (typeof window === "undefined") return

    const el = document.getElementById("bracky")
    if (!el) return

    const EXTRA_SPACING = -25
    const navEl = document.getElementById("landing-nav")
    const headerOffset = (navEl ? navEl.getBoundingClientRect().bottom : 90) + EXTRA_SPACING

    const elementY = el.getBoundingClientRect().top + window.scrollY
    const targetY = elementY - headerOffset

    window.scrollTo({ top: targetY, behavior: "smooth" })
  }, [])

  const [carets, setCarets] = useState<Array<{ id: number; pathX: number[]; pathY: number[] }>>([])
  const [caretCounter, setCaretCounter] = useState(0)
  const [lastTriggerTime, setLastTriggerTime] = useState(0)
  const [caretEnabled, setCaretEnabled] = useState(false)
  const [glowEnabled, setGlowEnabled] = useState<boolean>(false)
  const [glowBoost, setGlowBoost] = useState<boolean>(false)

  const generateOrganicPath = useCallback(() => {
    const xVariation = () => (Math.random() - 0.5) * 9.375
    const yPoints = [-5.625, -13.125, -22.5, -31.875, -41.25]
    const xPoints = [0, xVariation(), xVariation(), xVariation(), xVariation()]
    return { pathX: xPoints, pathY: yPoints }
  }, [])

  const triggerCaret = useCallback(
    (e?: React.MouseEvent | React.TouchEvent) => {
      e?.preventDefault()

      if (!caretEnabled) return

      const now = Date.now()
      if (now - lastTriggerTime < 50) return
      setLastTriggerTime(now)

      const clip = getBrackyNotificationClip()
      playSingleAudio(clip, 1, true)

      const newId = caretCounter + 1
      setCaretCounter(newId)

      const { pathX, pathY } = generateOrganicPath()
      const newCaret = { id: newId, pathX, pathY }

      setCarets((prev) => [...prev, newCaret])

      setTimeout(() => {
        setCarets((prev) => prev.filter((caret) => caret.id !== newId))
      }, 1250)
    },
    [caretEnabled, lastTriggerTime, caretCounter, generateOrganicPath],
  )

  useEffect(() => {
    if (typeof window === "undefined") return

    const enableGlow = () => {
      setGlowEnabled(true)
      setTimeout(() => {
        setCaretEnabled(true)
      }, 2000)
      setGlowBoost(true)
      setTimeout(() => setGlowBoost(false), 800)
    }
    window.addEventListener("brackyInitialComplete", enableGlow)

    return () => {
      window.removeEventListener("brackyInitialComplete", enableGlow)
    }
  }, [])

  const calculateHaloStyles = useCallback(() => {
    const haloOpacity = glowEnabled ? (activeMultiplier < 1 ? 0.75 : 1) : 0.35
    const BOOST_MULTIPLIER = glowBoost ? 1.5 : 1
    const sizeFactor = activeMultiplier > 1 ? 1 + (activeMultiplier - 1) * 2 : 1

    const BLUR_BASE = isMobile ? 220 : 360
    const SPREAD_BASE = isMobile ? 115 : 150
    const GLOW_STRENGTH = isMobile ? 1.1 : 0.8
    const DIRECTION_FACTOR = activeMultiplier < 1 ? 0.5 : 1

    const haloBlur = BLUR_BASE * sizeFactor * GLOW_STRENGTH * DIRECTION_FACTOR * BOOST_MULTIPLIER
    const haloSpread = SPREAD_BASE * sizeFactor * GLOW_STRENGTH * DIRECTION_FACTOR * BOOST_MULTIPLIER

    const INNER_GLOW_FACTOR = 0.25
    const innerBlur = haloBlur * INNER_GLOW_FACTOR
    const innerSpread = haloSpread * INNER_GLOW_FACTOR

    const shadowColor = "0,28,255"
    const outerBoxShadow = `0 0 ${haloBlur}px ${haloSpread}px rgba(${shadowColor},${haloOpacity})`
    const innerBoxShadow = `0 0 ${innerBlur}px ${innerSpread}px rgba(${shadowColor},${haloOpacity})`

    return `${innerBoxShadow}, ${outerBoxShadow}`
  }, [glowEnabled, glowBoost, activeMultiplier, isMobile])

  const haloBoxShadow = calculateHaloStyles()

  return (
    <div className="relative flex text-h2 flex-col items-center justify-center px-6 text-center py-6 select-none">
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Single line: "with" + Bracky Avatar + "@bracky" */}
        <div className="flex items-center justify-center gap-x-0 sm:gap-x-[0.2rem] md:gap-x-[0.3rem] lg:gap-x-[0.4rem] xl:gap-x-[0.68rem] mb-1">
          {/* "with" text */}
          <h1 className="font-extrabold text-white " style={{ letterSpacing: "-1px" }}>
            With
          </h1>

          {/* Bracky Avatar */}
          <div className="flex items-center">
            {/* Outer wrapper sets the baseline size purely with Tailwind breakpoints */}
            <div
              onMouseEnter={() => setIsAvatarHovered(true)}
              onMouseLeave={() => {
                setIsAvatarHovered(false)
                setIsAvatarPressed(false)
              }}
              onPointerDown={(e) => {
                setIsAvatarPressed(true)
                triggerCaret(e)
              }}
              onPointerUp={() => setIsAvatarPressed(false)}
              onPointerCancel={() => setIsAvatarPressed(false)}
              onPointerLeave={() => setIsAvatarPressed(false)}
              className="translate-x-[.05rem] relative scale-[1.06] sm:scale-[1.18] md:scale-[1.27] lg:scale-[1.3] xl:scale-[1.44] 2xl:scale-[1.53]"
            >
              {/* Inner wrapper handles interactive scaling */}
              <div
                className={`relative ${activeMultiplier !== 1 ? "transition-transform" : ""}`}
                style={{
                  transform: `scale(${computedScale})`,
                  transformOrigin: "center center",
                  transition: activeMultiplier !== 1 ? "transform 0.25s cubic-bezier(0.22, 1, 0.36, 1)" : undefined,
                }}
              >
                {/* Filled circle with box-shadow halo (solid core prevents gaps) */}
                <div
                  className="absolute inset-0 rounded-full pointer-events-none -z-10 transition-shadow ease-out"
                  style={{
                    transitionDuration: "750ms",
                    boxShadow: haloBoxShadow,
                    background: "#001CFF",
                    transform: "scale(.3)",
                  }}
                  aria-hidden="true"
                />
                <BrackyAvatar />

                {carets.map((caret) => (
                  <HeroCaretAnimation key={caret.id} id={caret.id} pathX={caret.pathX} pathY={caret.pathY} />
                ))}
              </div>
            </div>
          </div>

          {/* @bracky text */}
          <h1 className="ml-[-.15rem] font-extrabold text-white" style={{ letterSpacing: "-1px" }}>
            Bracky
          </h1>
        </div>
        <p className="max-w-80 z-0 text-white font-medium md:max-w-full mx-auto lg:max-w-full leading-[1.3] md:leading-tight mb-6 -mt-[.3rem] text-[1.0rem] sm:text-[1.02rem] md:text-[1rem] lg:text-[1.125rem] xl:text-[1.25rem]">
          <span className="opacity-90">
            The World's{" "}
            <span className="font-medium mr-[.1rem] ml-[.1rem] transform -skew-x-12 inline-block">
              #<span className="font-bold">1</span>
            </span>{" "}
          </span>
          <button
            type="button"
            onClick={handleScrollToBracky}
            className="relative z-20 cursor-pointer font-semibold shiny-text-ai-sports-agent bg-clip-text text-transparent focus:outline-hidden p-0 m-0 inline ml-[.05rem]"
            style={{ lineHeight: "inherit" }}
          >
            AI Sports Agent
          </button>
        </p>{" "}
      </div>
    </div>
  )
}
